import { useAuthStore } from '@/store/useAuthStore'
import { isAuthenticatedUser } from '@/lib/auth/isAuthenticated'

export type SessionStatus =
  | 'idle'
  | 'loading'
  | 'authenticated'
  | 'anonymous'
  | 'transitioning'

/** Dérive sessionStatus depuis les flags Zustand (source unique pour gates). */
export function deriveSessionStatus(state: {
  sessionBootstrapped: boolean
  sessionHydrated: boolean
  sessionTransitioning: boolean
  user: { id?: string } | null
}): SessionStatus {
  if (state.sessionTransitioning) {
    return 'transitioning'
  }
  if (!state.sessionBootstrapped) {
    return state.sessionHydrated ? 'loading' : 'idle'
  }
  if (isAuthenticatedUser(state.user, state.sessionHydrated)) {
    return 'authenticated'
  }
  return 'anonymous'
}

export function useSessionStatus(): SessionStatus {
  const sessionBootstrapped = useAuthStore((s) => s.sessionBootstrapped)
  const sessionHydrated = useAuthStore((s) => s.sessionHydrated)
  const sessionTransitioning = useAuthStore((s) => s.sessionTransitioning)
  const user = useAuthStore((s) => s.user)

  return deriveSessionStatus({
    sessionBootstrapped,
    sessionHydrated,
    sessionTransitioning,
    user,
  })
}

/** Loader plein écran RouteGuard — pas bloquer sur sessionHydrated après bootstrap anonyme (401). */
export function shouldShowProtectedRouteLoading(input: {
  isPublic: boolean
  persistHydrated: boolean
  bootstrapInFlight: boolean
  sessionTransitioning: boolean
  sessionSilentSwap: boolean
  sessionBootstrapped: boolean
  sessionStatus: SessionStatus
}): boolean {
  if (input.isPublic) {
    return false
  }
  if (!input.persistHydrated) {
    return true
  }
  if (input.bootstrapInFlight) {
    return true
  }
  if (input.sessionSilentSwap) {
    return false
  }
  if (input.sessionTransitioning) {
    return true
  }
  if (
    !input.sessionBootstrapped &&
    (input.sessionStatus === 'loading' || input.sessionStatus === 'idle')
  ) {
    return true
  }
  return false
}
